import { ApiError, Unauthenticated } from '../../../api.js';
import { viewFetch } from '../../../view-as.js';
import { withDeadline } from '../../deadline.js';
import type { AccessPageData } from '../../access/types.js';
import type { DirectoryUser, Invite, SetupPerson } from '../../../onboarding/api.js';
import type { Added, AddPersonAdditions, ReadBack } from '../contract.js';

export interface AddSetup extends Pick<AccessPageData, 'groups' | 'catalogue'> { people: SetupPerson[]; policyPublished: boolean; keeperFirstName: string | null }
export type AddRequest = AddPersonAdditions & { objectId: string; managerPrincipalId: string | null; role: 'member'; grants: string[] };
export type PreviewRequest = Pick<AddPersonAdditions, 'groupId' | 'gives' | 'holds'>;
async function json<T>(res: Response): Promise<T> {
  if (res.status === 401) throw new Unauthenticated();
  if (!res.ok) {
    const data = await res.json().catch(() => ({})) as { error?: unknown };
    throw new ApiError(res.status, typeof data.error === 'string' ? data.error : `${res.status} ${res.statusText}`);
  }
  return await res.json() as T;
}
const root = '/api/admin/onboarding';
const query = (store: string) => `store=${encodeURIComponent(store)}`;
function read<T>(path: string, signal?: AbortSignal): Promise<T> {
  return withDeadline(path, { credentials: 'same-origin' }, json<T>, signal);
}
function post<T>(store: string, path: string, body: object): Promise<T> {
  return viewFetch(`${root}${path}`, { method: 'POST', credentials: 'same-origin', headers: { 'content-type': 'application/json' }, body: JSON.stringify({ store, ...body }) }).then(json<T>);
}
export const addApi = {
  async setup(store: string, signal?: AbortSignal): Promise<AddSetup> {
    const [setup, access] = await Promise.all([
      read<{ people: SetupPerson[]; policyPublished: boolean; keeperFirstName?: string | null }>(`${root}/setup?${query(store)}`, signal),
      read<AccessPageData>(`/api/admin/access?${query(store)}`, signal),
    ]);
    return { people: setup.people, policyPublished: setup.policyPublished, keeperFirstName: setup.keeperFirstName ?? null, groups: access.groups, catalogue: access.catalogue };
  },
  directory: (store: string, q: string, signal?: AbortSignal) => read<{ users: DirectoryUser[] }>(`${root}/directory?${query(store)}&q=${encodeURIComponent(q)}`, signal),
  preview: (store: string, body: PreviewRequest, signal?: AbortSignal) => withDeadline(`${root}/preview`, { method: 'POST', credentials: 'same-origin', headers: { 'content-type': 'application/json' }, body: JSON.stringify({ store, ...body }) }, json<ReadBack>, signal),
  add: (store: string, body: AddRequest) => post<Added>(store, '/people', body),
  invite: (store: string, id: string) => post<Added>(store, `/people/${encodeURIComponent(id)}/invite`, { send: true }),
  copy: (store: string, id: string) => post<{ invite: Invite }>(store, `/people/${encodeURIComponent(id)}/invite`, { copy: true }),
};
